/**
 * Remembered label printing choices, kept in localStorage on this machine.
 *
 * The printer is remembered per template, not globally: each LabelWriter holds
 * one roll size and a template is designed for one size, so the thing a user
 * repeats is the template to printer pairing. Printer names are local to the
 * machine running DYMO Connect, which is why this lives in the browser and not
 * in a server side preference.
 *
 * Every read and write is wrapped because storage can be blocked or full; a
 * failed read just means no preselection.
 */

import type { DymoPrinter } from "./dymo";

export const LAST_TEMPLATE_KEY = "labels:last-template-id";
export const lastPrinterKey = (labelId: number | string) =>
  `labels:last-printer:${labelId}`;

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error("error", error);
    return null;
  }
};

const write = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.error("error", error);
  }
};

/** The last template printed with, or null when none is stored or it's garbage. */
export const getLastLabelId = (): number | null => {
  const raw = read(LAST_TEMPLATE_KEY);
  if (raw === null || !/^\d+$/.test(raw)) return null;
  const id = Number(raw);
  return id > 0 ? id : null;
};

export const setLastLabelId = (labelId: number | string) =>
  write(LAST_TEMPLATE_KEY, String(labelId));

export const getLastPrinterForLabel = (
  labelId: number | string
): string | null => read(lastPrinterKey(labelId)) || null;

export const setLastPrinterForLabel = (
  labelId: number | string,
  printerName: string
) => write(lastPrinterKey(labelId), printerName);

/**
 * The printer to preselect for a template: the remembered one if it's still
 * listed, else the first listed printer, else nothing.
 */
export const resolveDefaultPrinter = (
  labelId: number | string | null | undefined,
  printers: DymoPrinter[] | undefined
): string | undefined => {
  if (!printers || printers.length === 0) return undefined;
  const remembered =
    labelId === null || labelId === undefined
      ? null
      : getLastPrinterForLabel(labelId);
  if (remembered && printers.some((p) => p.name === remembered)) {
    return remembered;
  }
  return printers[0].name;
};
